/**
 * ThemeContext — light/dark/system preference.
 *
 * Note: DESIGN_SPEC §9 forbids a light mode in v1; the carbon-graphite +
 * paper-cream inversion is the brand. The context exposes the surface so
 * a high-contrast variant ([data-contrast="high"]) can be toggled and so
 * `prefers-color-scheme` can be observed without v1 ever rendering light.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";

export type ThemePreference = "dark" | "system";
export type ContrastPreference = "normal" | "high";

export interface ThemeContextValue {
  readonly theme: ThemePreference;
  readonly contrast: ContrastPreference;
  readonly prefersReducedMotion: boolean;
  readonly setContrast: (contrast: ContrastPreference) => void;
}

const defaultValue: ThemeContextValue = {
  theme: "dark",
  contrast: "normal",
  prefersReducedMotion: false,
  setContrast: () => {
    /* stub */
  },
};

const ThemeContext = createContext<ThemeContextValue>(defaultValue);

export interface ThemeProviderProps {
  readonly children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [contrast, setContrast] = useState<ContrastPreference>("normal");
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return;
    }
    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReducedMotion(motionQuery.matches);
    const onChange = (event: MediaQueryListEvent): void => {
      setPrefersReducedMotion(event.matches);
    };
    motionQuery.addEventListener("change", onChange);
    return () => motionQuery.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }
    document.documentElement.dataset.contrast = contrast;
    document.documentElement.dataset.theme = "dark";
  }, [contrast]);

  const handleSetContrast = useCallback((next: ContrastPreference) => {
    setContrast(next);
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme: "dark",
      contrast,
      prefersReducedMotion,
      setContrast: handleSetContrast,
    }),
    [contrast, prefersReducedMotion, handleSetContrast],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext);
}
